import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { supabase } from "@/lib/supabase";
import { canPay } from "@/lib/billing-payer";
import { CreditTopupDialog } from "@/components/settings/CreditTopupDialog";
import {
  type CreditBalanceAlertState,
  creditBalanceAlertCopy,
  creditBalanceAlertDismissKey,
  parseCreditBalanceAlert,
} from "@/lib/credit-balance-alerts";

const REFRESH_MS = 3 * 60 * 1000;
const REFRESH_AFTER_PAYMENT_MS = 5000;

function readDismissed(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeDismissed(key: string): void {
  try {
    window.localStorage.setItem(key, "1");
  } catch {
    return;
  }
}

export function CreditBalanceAlertBanner() {
  const { activeWorkspaceId, agent, user, userRole } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [alert, setAlert] = useState<CreditBalanceAlertState | null>(null);
  const [dismissedNow, setDismissedNow] = useState<Set<string>>(new Set());
  const [topupOpen, setTopupOpen] = useState(false);
  const enabled = brand.id === "flow" && !!activeWorkspaceId;

  const load = useCallback(async () => {
    if (!enabled) {
      setAlert(null);
      return;
    }
    const { data, error } = await supabase.rpc("current_credit_balance_alert" as never);
    if (error) {
      console.error("[CreditBalanceAlertBanner]", error.message);
      setAlert(null);
      return;
    }
    setAlert(parseCreditBalanceAlert(data, activeWorkspaceId));
  }, [enabled, activeWorkspaceId]);

  useEffect(() => {
    void load();
    if (!enabled) return;
    const interval = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(interval);
  }, [load, enabled]);

  if (!enabled) return null;

  const payer = canPay(userRole);
  const dialog = payer ? (
    <CreditTopupDialog
      open={topupOpen}
      onOpenChange={setTopupOpen}
      orgId={activeWorkspaceId}
      payerEmail={agent?.email || user?.email || ""}
      onPaid={() => { window.setTimeout(() => void load(), REFRESH_AFTER_PAYMENT_MS); }}
    />
  ) : null;

  const key = alert ? creditBalanceAlertDismissKey(alert) : null;
  if (!alert || !key || dismissedNow.has(key) || readDismissed(key)) return dialog;

  const { title, detail } = creditBalanceAlertCopy(alert);
  const dismiss = () => {
    writeDismissed(key);
    setDismissedNow((prev) => new Set(prev).add(key));
  };
  const recharge = () => (payer ? setTopupOpen(true) : navigate("/app/settings/account"));

  return (
    <>
      <div
        role="status"
        className="shadow-panel mx-3 mt-3 rounded-xl bg-amber-500/15 px-4 py-2.5 flex items-center justify-between gap-3 text-sm"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Wallet className="h-4 w-4 text-amber-600 shrink-0" aria-hidden="true" />
          <p className="text-amber-700 dark:text-amber-400 font-medium">
            <span className="font-bold">{title}</span> {detail}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={recharge}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
          >
            Recargar saldo
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="p-1 rounded-md text-amber-700 hover:bg-amber-500/20 dark:text-amber-400"
            aria-label="Cerrar aviso"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      {dialog}
    </>
  );
}
